'use client';

import { useEffect, useState } from 'react';

/**
 * Detected client platform. We only care about distinguishing between the
 * three install paths (iOS Safari A2HS, iOS non-Safari deeplink, Chromium
 * `beforeinstallprompt`) plus a fallback bucket.
 */
export type InstallPlatform =
  | 'ios-safari'
  | 'ios-other' // Chrome/Firefox/Edge/etc on iOS — all WebKit, no A2HS UI
  | 'android-chrome' // Chrome/Edge/Brave/Samsung on Android — beforeinstallprompt
  | 'android-other' // Firefox Android, etc — usually no install path
  | 'desktop-chromium' // Chrome/Edge/Brave/Opera on desktop — beforeinstallprompt
  | 'desktop-other' // Safari/Firefox desktop — no PWA install
  | 'unknown';

/**
 * Native event captured from `beforeinstallprompt`. Typed loosely because the
 * BeforeInstallPromptEvent type isn't part of lib.dom.d.ts everywhere yet.
 */
export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export interface InstallContext {
  /** Coarse platform bucket — drives which install UI to render. */
  platform: InstallPlatform;
  /** True when the page is running as an installed PWA. */
  isStandalone: boolean;
  /** Convenience flag: covers Android + iOS (the user-facing definition of "mobile" for installability). */
  isMobile: boolean;
  /**
   * Captured `beforeinstallprompt` event, if the browser fired one. Calling
   * `.prompt()` is the one-tap install on Chromium platforms. `null` until
   * the event fires (and after it's been consumed).
   */
  installPromptEvent: BeforeInstallPromptEvent | null;
  /** True once the page has hydrated client-side (avoid SSR hydration drift). */
  hasMounted: boolean;
}

function detectPlatform(ua: string): InstallPlatform {
  // Apple platforms — Safari, Chrome (CriOS), Firefox (FxiOS), Edge (EdgiOS)
  // all use WebKit underneath, so we have to UA-sniff. iPad on iPadOS 13+
  // reports as Mac in `userAgent` but exposes touch — handled below.
  const isAppleTouch = /iPad|iPhone|iPod/.test(ua);
  const isIpadOSDesktopUA =
    typeof navigator !== 'undefined' &&
    /Macintosh/.test(ua) &&
    typeof navigator.maxTouchPoints === 'number' &&
    navigator.maxTouchPoints > 1;

  if (isAppleTouch || isIpadOSDesktopUA) {
    if (/CriOS|FxiOS|EdgiOS|OPiOS|YaBrowser/.test(ua)) {
      return 'ios-other';
    }
    // Genuine Safari: contains "Safari/" but not any of the *iOS suffixes
    // above. Embedded WKWebViews (Twitter, Instagram, etc.) typically miss
    // the "Safari/" token so we treat them as ios-other (deep-link path).
    return /Safari\//.test(ua) ? 'ios-safari' : 'ios-other';
  }

  if (/Android/.test(ua)) {
    // Anything Chromium-based on Android fires `beforeinstallprompt`. Detect
    // by the chromium UA tokens; fall through to "android-other" for Firefox.
    if (/Chrome|EdgA|SamsungBrowser|Brave|OPR/.test(ua)) {
      return 'android-chrome';
    }
    return 'android-other';
  }

  // Desktop. Chromium-family fires `beforeinstallprompt`; Safari and Firefox
  // do not support PWA install.
  if (/Edg\/|Chrome\/|Chromium|OPR\/|Brave/.test(ua)) {
    return 'desktop-chromium';
  }
  if (/Safari\//.test(ua) || /Firefox\//.test(ua)) {
    return 'desktop-other';
  }

  return 'unknown';
}

function detectStandalone(): boolean {
  if (typeof window === 'undefined') return false;

  // Primary signal: the `display-mode: standalone` media query. It is part
  // of the CSS spec, supported on iOS Safari 13+, and — critically — is
  // *per-window*. The standalone PWA window matches `standalone`, while
  // a separate Safari tab opened against the same origin correctly matches
  // `browser` even when both are alive on the device.
  if (typeof window.matchMedia === 'function') {
    return window.matchMedia('(display-mode: standalone)').matches;
  }

  // Fallback for ancient WebViews without `matchMedia`. iOS Safari has
  // exposed the non-standard `navigator.standalone` since iOS 4, but we
  // intentionally do NOT consult it when matchMedia is available because
  // it has been observed to report `true` in regular Safari sessions
  // after the user has previously installed the PWA — it appears the
  // value can stick across Safari sessions until a full process restart.
  // The media query above is reliably accurate in those scenarios.
  return (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
}

/**
 * Detects the user's install context and captures the `beforeinstallprompt`
 * event for one-tap install on Chromium browsers.
 *
 * SSR-safe: returns a placeholder (`platform: 'unknown'`, `hasMounted: false`)
 * during SSR / first render, then re-renders with real values after mount.
 * Callers should gate UI on `hasMounted` to avoid hydration mismatches.
 */
export function useInstallContext(): InstallContext {
  const [hasMounted, setHasMounted] = useState(false);
  const [platform, setPlatform] = useState<InstallPlatform>('unknown');
  const [isStandalone, setIsStandalone] = useState(false);
  const [installPromptEvent, setInstallPromptEvent] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    setHasMounted(true);
    setPlatform(detectPlatform(navigator.userAgent));
    setIsStandalone(detectStandalone());

    const handleBeforeInstallPrompt = (event: Event) => {
      // Suppress the browser's default mini-infobar so we can present our own
      // CTA from the Settings > Install page. Standard PWA pattern.
      event.preventDefault();
      setInstallPromptEvent(event as BeforeInstallPromptEvent);
    };

    const handleAppInstalled = () => {
      // Once installed, the captured event is no longer valid and the page
      // will eventually be reloaded in standalone mode. Clear our state so
      // callers stop offering the install button.
      setInstallPromptEvent(null);
      setIsStandalone(detectStandalone());
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // The display-mode can flip without a reload (rare, but possible when
    // the OS launches the PWA in standalone after a manual install). Listen
    // so we don't keep nagging an installed user.
    const mql = typeof window.matchMedia === 'function' ? window.matchMedia('(display-mode: standalone)') : null;
    const handleDisplayModeChange = () => setIsStandalone(detectStandalone());
    mql?.addEventListener?.('change', handleDisplayModeChange);

    // Re-detect when the page becomes visible again. iOS in particular can
    // hand the same WebKit process between a Safari tab and the standalone
    // PWA window; without a re-check on `visibilitychange`, a Safari tab
    // that was previously evaluated as standalone (or vice versa) keeps
    // its stale flag until reload. Cheap to recompute.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setIsStandalone(detectStandalone());
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      mql?.removeEventListener?.('change', handleDisplayModeChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const isMobile =
    platform === 'ios-safari' ||
    platform === 'ios-other' ||
    platform === 'android-chrome' ||
    platform === 'android-other';

  return {
    platform,
    isStandalone,
    isMobile,
    installPromptEvent,
    hasMounted
  };
}
